import { useQuery } from '@tanstack/react-query';
import { getTotalCounts } from './DashboardAnalytics';
import { getRolloutActivitiesData } from './DashboardAnalytics';
import { getRolloutProjectsData } from './DashboardAnalytics';
import { getAffectedFilesByRepos } from './DashboardAnalytics';
import { getBugsinRolloutAnalytics } from './DashboardAnalytics';

/**
 * Fetches overall analytics data for rollout summary.
 * Static data – does not depend on external params.
 */
export const useAnalyticsData = () => {
  return useQuery({
    queryKey: ['analyticsData'],
    queryFn: () =>
      getTotalCounts({
        moduleName: 'RolloutSummary',
        isServiceWorker: false,
        data: {},
      }),
    refetchOnWindowFocus: true,
    staleTime: 60000,
    select: (response) => {
      if (response?.isSuccess) {
        const stats = response?.stats || {};
        return {
          totalRollouts: stats.totalRollouts ?? '--',
          totalfiles: stats.totalFiles ?? '--',
          uniqueIssues: stats.uniqueIssues ?? '--',
          totalprojects: stats.totalProjects ?? '--',
          totalRepos: stats.totalRepos ?? '--',
        };
      }
      throw new Error('Failed to fetch analytics data');
    },
  });
};

/**
 * Fetches overall analytics data for rollout summary.
 * Static data – does not depend on external params.
 */
export const useRolloutActivities = (dates) => {
  return useQuery({
    queryKey: ['rolloutActivities', dates], // enables automatic refetch when dates change
    queryFn: () =>
      getRolloutActivitiesData({
        moduleName: 'RolloutActivities',
        isServiceWorker: false,
        data: { ...dates },
      }),
    enabled: !!dates, // optional: only run if dates is defined
    staleTime: 60 * 1000, // 1 minute caching
    refetchOnWindowFocus: false,
    select: (response) => response?.payload, // extract the part you need
  });
};

/**
 * Fetches rollout project analytics grouped by project name.
 * @param {Object} dates - date filters for the query
 */
export const useRolloutProjects = (dates) => {
  return useQuery({
    queryKey: ['rolloutProjectsData', dates],
    queryFn: () =>
      getRolloutProjectsData({
        moduleName: 'RolloutsByProject',
        isServiceWorker: false,
        data: { ...dates },
      }),
    enabled: !!dates, // only runs when `dates` is defined
    staleTime: 60 * 1000, // 1 minute caching
    refetchOnWindowFocus: false,
    select: (response) => response?.payload, // return only relevant data
  });
};


/**
 * Fetches affected files grouped by repository based on date filters.
 * @param {Object} dates - date filters for the query
 */
export const useAffectedFilesByRepos = (dates) => {
  return useQuery({
    queryKey: ['affectedFilesData', dates],
    queryFn: () =>
      getAffectedFilesByRepos({
        moduleName: 'AffectedFilesData',
        isServiceWorker: false,
        data: { ...dates },
      }),
    enabled: !!dates, // only runs when `dates` is defined
    staleTime: 60 * 1000, // 1 minute caching
    refetchOnWindowFocus: false,
    select: (response) => response?.payload, // return only relevant data
  });
};

/**
 * Fetches bugs reported in rollouts, grouped by severity or project.
 * @param {Object} dates - date filters for the query
 */
export const useBugsinRolloutAnalytics = (dates) => {
  return useQuery({
    queryKey: ['bugsinRollouts', dates],
    queryFn: () =>
      getBugsinRolloutAnalytics({
        moduleName: 'BugsinRolloutsData',
        isServiceWorker: false,
        data: { ...dates },
      }),
    enabled: !!dates, // only runs when `dates` is defined
    staleTime: 60 * 1000, // 1 minute caching
    refetchOnWindowFocus: false,
    select: (response) => response?.payload, // return only relevant data
  });
};
